import { useTu } from "@src/appV2/i18n/useTu";
import { type ShiftMigrationCounts } from "@clipboard-health/contract-backend-main";
import { Alert, Box, Stack, type Theme } from "@mui/material";
import { Text } from "../../components/Text";
import { QualificationCaption } from "./QualificationCaption";

interface ShiftMigrationResultStepProps {
  variant: "success" | "error";
  counts?: ShiftMigrationCounts;
  qualification?: string;
  errorMessage?: string;
}

interface CountRowProps {
  label: string;
  value: number;
  emphasis?: boolean;
  indent?: boolean;
}

function CountRow(props: CountRowProps) {
  const { label, value, emphasis, indent } = props;
  const secondary = (theme: Theme) => (indent ? { color: theme.palette.text.secondary } : {});

  return (
    <>
      <Text semibold={emphasis} sx={[{ pl: indent ? 3 : 0 }, secondary]} variant="body2">
        {label}
      </Text>
      <Text semibold={emphasis} sx={[{ textAlign: "right" }, secondary]} variant="body2">
        {value}
      </Text>
    </>
  );
}

function CountGrid(props: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `8rem max-content`,
        rowGap: 1,
        justifyContent: "start",
      }}
    >
      {props.children}
    </Box>
  );
}

export function ShiftMigrationResultStep(props: ShiftMigrationResultStepProps) {
  const { tu } = useTu("shiftMigration");

  const { variant, counts, qualification, errorMessage } = props;

  if (variant === "error") {
    return (
      <Stack spacing={3}>
        <Alert severity="error">{errorMessage ?? "Migration failed. Please try again."}</Alert>
        <Text variant="body2">
          {tu("The migration was not applied. Try again and run a new preview.")}
        </Text>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Alert severity="success">{tu("Migration completed successfully.")}</Alert>
      {counts && (
        <Stack spacing={1}>
          <Text semibold variant="body1">
            {tu("Shifts migrated")}
          </Text>
          <QualificationCaption qualification={qualification} />
          <CountGrid>
            <CountRow emphasis label={tu("Total")} value={counts.migratable.total} />
            <CountRow indent label={tu("Verified")} value={counts.migratable.verified} />
            <CountRow indent label={tu("Booked")} value={counts.migratable.booked} />
            <CountRow indent label={tu("Open")} value={counts.migratable.open} />
          </CountGrid>
        </Stack>
      )}
    </Stack>
  );
}
